import React, { useEffect } from 'react';
import { useMutateObserver } from '@rc-component/mutate-observer';
import { useEvent } from '@rc-component/util';
import { clsx } from 'clsx';
import { useComponentConfig } from '../config-provider/context';
import { useToken } from '../theme/internal';
import WatermarkContext from './context';
import useClips, { FontGap } from './useClips';
import useRafDebounce from './useRafDebounce';
import useSingletonCache from './useSingletonCache';
import useWatermark from './useWatermark';
import { getCanvasFont, getContentLines, getPixelRatio, reRendering } from './utils';
/**
 * Only return `next` when size changed.
 * This is only used for elements compare, not a shallow equal!
 */
function getSizeDiff(prev, next) {
    return prev.size === next.size ? prev : next;
}
const DEFAULT_GAP_X = 100;
const DEFAULT_GAP_Y = 100;
const WATERMARK_Z_INDEX_OFFSET = 1;
const fixedStyle = {
    position: 'relative',
    overflow: 'hidden',
};
const Watermark = (props) => {
    const { zIndex, rotate = -22, width, height, image, content, font = {}, style, className, rootClassName, gap = [DEFAULT_GAP_X, DEFAULT_GAP_Y], offset, children, inherit = true, onRemove, } = props;
    const { className: contextClassName, style: contextStyle } = useComponentConfig('watermark');
    const mergedStyle = {
        ...fixedStyle,
        ...contextStyle,
        ...style,
    };
    const [, token] = useToken();
    // Keep Watermark above content layers while staying below popup base.
    const mergedZIndex = zIndex ?? token.zIndexPopupBase - WATERMARK_Z_INDEX_OFFSET;
    const { color = token.colorFill, fontSize = token.fontSizeLG, fontWeight = 'normal', fontStyle = 'normal', fontFamily = 'sans-serif', textAlign = 'center', } = font;
    const mergedFont = React.useMemo(() => ({
        color,
        fontSize,
        fontWeight,
        fontStyle,
        fontFamily,
        textAlign,
    }), [color, fontSize, fontWeight, fontStyle, fontFamily, textAlign]);
    const contentLines = React.useMemo(() => getContentLines(content, mergedFont), [content, mergedFont]);
    const [gapX = DEFAULT_GAP_X, gapY = DEFAULT_GAP_Y] = gap;
    const gapXCenter = gapX / 2;
    const gapYCenter = gapY / 2;
    const offsetLeft = offset?.[0] ?? gapXCenter;
    const offsetTop = offset?.[1] ?? gapYCenter;
    const markStyle = React.useMemo(() => {
        const mergedMarkStyle = {
            zIndex: mergedZIndex,
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            backgroundRepeat: 'repeat',
        };
        /** Calculate the style of the offset */
        let positionLeft = offsetLeft - gapXCenter;
        let positionTop = offsetTop - gapYCenter;
        if (positionLeft > 0) {
            mergedMarkStyle.left = `${positionLeft}px`;
            mergedMarkStyle.width = `calc(100% - ${positionLeft}px)`;
            positionLeft = 0;
        }
        if (positionTop > 0) {
            mergedMarkStyle.top = `${positionTop}px`;
            mergedMarkStyle.height = `calc(100% - ${positionTop}px)`;
            positionTop = 0;
        }
        mergedMarkStyle.backgroundPosition = `${positionLeft}px ${positionTop}px`;
        return mergedMarkStyle;
    }, [mergedZIndex, offsetLeft, gapXCenter, offsetTop, gapYCenter]);
    const [container, setContainer] = React.useState();
    // Used for nest case like Modal, Drawer
    const [subElements, setSubElements] = React.useState(() => new Set());
    // Nest elements should also support watermark
    const targetElements = React.useMemo(() => {
        const list = container ? [container] : [];
        return [...list, ...Array.from(subElements)];
    }, [container, subElements]);
    // ============================ Content =============================
    /**
     * Get the width and height of the watermark. The default values are as follows
     * Image: [120, 64]; Content: It's calculated by content;
     */
    const getMarkSize = (ctx) => {
        let defaultWidth = 120;
        let defaultHeight = 64;
        if (!image && ctx.measureText) {
            if (contentLines.length) {
                const sizes = contentLines.map(({ text, font: lineFont }) => {
                    ctx.font = getCanvasFont(lineFont);
                    const metrics = ctx.measureText(text);
                    return [metrics.width, metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent];
                });
                defaultWidth = Math.ceil(Math.max(...sizes.map((size) => size[0])));
                defaultHeight =
                    Math.ceil(sizes.reduce((total, size) => total + size[1], 0)) +
                        (contentLines.length - 1) * FontGap;
            }
            else {
                defaultWidth = 0;
                defaultHeight = 0;
            }
        }
        return [width ?? defaultWidth, height ?? defaultHeight];
    };
    const getClips = useClips();
    const getClipsCache = useSingletonCache();
    const [watermarkInfo, setWatermarkInfo] = React.useState(null);
    // Generate new Watermark content
    const renderWatermark = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
            const ratio = getPixelRatio();
            const [markWidth, markHeight] = getMarkSize(ctx);
            const drawCanvas = (drawContent) => {
                const params = [
                    drawContent || [],
                    rotate,
                    ratio,
                    markWidth,
                    markHeight,
                    gapX,
                    gapY,
                ];
                const [nextClips, clipWidth] = getClipsCache(params, () => getClips(...params));
                setWatermarkInfo([nextClips, clipWidth]);
            };
            if (image) {
                const img = new Image();
                img.onload = () => {
                    drawCanvas(img);
                };
                img.onerror = () => {
                    drawCanvas(contentLines);
                };
                img.crossOrigin = 'anonymous';
                img.referrerPolicy = 'no-referrer';
                img.src = image;
            }
            else {
                drawCanvas(contentLines);
            }
        }
    };
    const syncWatermark = useRafDebounce(renderWatermark);
    // ============================= Effect =============================
    // Append watermark to the container
    const [appendWatermark, removeWatermark, isWatermarkEle] = useWatermark(markStyle, onRemove);
    useEffect(() => {
        if (watermarkInfo) {
            targetElements.forEach((holder) => {
                appendWatermark(watermarkInfo[0], watermarkInfo[1], holder);
            });
        }
    }, [watermarkInfo, targetElements]);
    // ============================ Observe =============================
    const onMutate = useEvent((mutations) => {
        mutations.forEach((mutation) => {
            if (reRendering(mutation, isWatermarkEle)) {
                syncWatermark();
            }
            else if (mutation.target === container && mutation.attributeName === 'style') {
                // We've only force container not modify.
                // Not consider nest case.
                const keyStyles = Object.keys(fixedStyle);
                for (let i = 0; i < keyStyles.length; i += 1) {
                    const key = keyStyles[i];
                    const oriValue = mergedStyle[key];
                    const currentValue = container.style[key];
                    if (oriValue && oriValue !== currentValue) {
                        container.style[key] = oriValue;
                    }
                }
            }
        });
    });
    useMutateObserver(targetElements, onMutate);
    useEffect(syncWatermark, [
        rotate,
        mergedZIndex,
        width,
        height,
        image,
        contentLines,
        gapX,
        gapY,
        offsetLeft,
        offsetTop,
    ]);
    // ============================ Context =============================
    const watermarkContext = React.useMemo(() => ({
        add: (ele) => {
            setSubElements((prev) => {
                const clone = new Set(prev);
                clone.add(ele);
                return getSizeDiff(prev, clone);
            });
        },
        remove: (ele) => {
            removeWatermark(ele);
            setSubElements((prev) => {
                const clone = new Set(prev);
                clone.delete(ele);
                return getSizeDiff(prev, clone);
            });
        },
    }), []);
    // ============================= Render =============================
    const childNode = inherit ? (React.createElement(WatermarkContext.Provider, { value: watermarkContext }, children)) : (children);
    return (React.createElement("div", { ref: setContainer, className: clsx(className, contextClassName, rootClassName), style: mergedStyle }, childNode));
};
if (process.env.NODE_ENV !== 'production') {
    Watermark.displayName = 'Watermark';
}
export default Watermark;
